import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BudgetApiClient } from '../api/client';
import { getSubTypeColor, SONSTIGES_COLOR, SONSTIGES_ID } from '../utils/colors';
import type { BudgetData, FilterState, ExpenditureTypeData, SelectedTitle, TitleData } from '../types';

export interface UseBudgetDataResult {
  data: BudgetData | null;
  subData: ExpenditureTypeData[] | null;
  titleData: TitleData[] | null;
  subColorMap: Map<string, string> | null;
  titleColorMap: Map<string, string> | null;
  isLoading: boolean;
  isLoadingSubData: boolean;
  isLoadingTitleData: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBudgetData(
  apiClient: BudgetApiClient,
  filters: FilterState,
  drillDownType: string | null,
  drillDownSubType: string | null = null,
  selectedTitle: SelectedTitle | null = null,
  brokenOutTitleIds: string[] = []
): UseBudgetDataResult {
  const [data, setData] = useState<BudgetData | null>(null);
  const [subData, setSubData] = useState<ExpenditureTypeData[] | null>(null);
  const [titleData, setTitleData] = useState<TitleData[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subDataForType, setSubDataForType] = useState<string | null>(null);
  const [isLoadingSubData, setIsLoadingSubData] = useState(false);
  const [isLoadingTitleData, setIsLoadingTitleData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const subRequestIdRef = useRef(0);
  const titleRequestIdRef = useRef(0);
  // Mirror of titleData, read inside the fetch effect without making it a dep.
  const titleDataRef = useRef<TitleData[] | null>(null);
  // The title scope the last fetch ran for. When ONLY brokenOutTitleIds changes
  // (same scope, data already on screen — e.g. the Sonstiges picker breaking a
  // title out), we refetch in the background and swap the result in instead of
  // clearing to a spinner. That keeps the chart — and an open picker — visible
  // across several break-outs in a row.
  const prevTitleScopeRef = useRef<{
    filters: FilterState;
    drillDownType: string | null;
    drillDownSubType: string | null;
    titleId: string | undefined;
  } | null>(null);

  // Get the title ID if a title is selected
  const titleId = selectedTitle?.id || undefined;

  // Clear drill-down data when budget type changes to prevent stale data triggering auto-skip
  useEffect(() => {
    setSubData(null);
    setSubDataForType(null);
    setTitleData(null);
  }, [filters.type]);

  const fetchData = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiClient.getBudgetData(filters, titleId);
      setData(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, filters, titleId]);

  // Fetch main data when filters change
  useEffect(() => {
    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  // Fetch sub-data when drill-down type changes
  useEffect(() => {
    if (!drillDownType) {
      setSubData(null);
      setSubDataForType(null);
      setIsLoadingSubData(false);
      return;
    }

    const currentRequestId = ++subRequestIdRef.current;
    setSubData(null);  // Clear stale data immediately
    setSubDataForType(null);
    setIsLoadingSubData(true);

    const fetchSubData = async () => {
      try {
        const result = await apiClient.getSubExpenditureBreakdown(filters, drillDownType, titleId);
        if (currentRequestId !== subRequestIdRef.current) return;
        setSubData(result);
        setSubDataForType(drillDownType);
      } catch (err) {
        if (currentRequestId !== subRequestIdRef.current) return;
        console.error('Failed to fetch sub-data:', err);
        setSubData(null);
        setSubDataForType(null);
      } finally {
        if (currentRequestId === subRequestIdRef.current) {
          setIsLoadingSubData(false);
        }
      }
    };

    fetchSubData();
  }, [apiClient, filters, drillDownType, titleId]);

  // Keep titleDataRef in step with titleData. Declared before the fetch effect
  // so on the render that changes brokenOutTitleIds it reflects the data still
  // on screen when the fetch effect below reads it.
  useEffect(() => {
    titleDataRef.current = titleData;
  }, [titleData]);

  // Fetch title data when drilling down to third level
  useEffect(() => {
    if (!drillDownType) {
      setTitleData(null);
      setIsLoadingTitleData(false);
      prevTitleScopeRef.current = null;
      return;
    }

    // For entries without sub-types (e.g., Einnahmen), drillDownSubType will be empty string
    // We still need to fetch title data in this case
    if (drillDownSubType === null) {
      setTitleData(null);
      setIsLoadingTitleData(false);
      prevTitleScopeRef.current = null;
      return;
    }

    // A break-out toggle changes only brokenOutTitleIds — same scope, data
    // already shown. Refetch quietly (no clear, no spinner) so the picker and
    // chart stay put; a scope change (drill/filter) still hard-loads.
    const prev = prevTitleScopeRef.current;
    const scopeUnchanged =
      prev !== null &&
      prev.filters === filters &&
      prev.drillDownType === drillDownType &&
      prev.drillDownSubType === drillDownSubType &&
      prev.titleId === titleId;
    const isSoftRefetch = scopeUnchanged && titleDataRef.current !== null;
    prevTitleScopeRef.current = { filters, drillDownType, drillDownSubType, titleId };

    const currentRequestId = ++titleRequestIdRef.current;
    if (!isSoftRefetch) {
      setTitleData(null);  // Clear stale data immediately
      setIsLoadingTitleData(true);
    }

    const fetchTitleData = async () => {
      try {
        const result = await apiClient.getTitleBreakdown(filters, drillDownType, drillDownSubType, titleId, brokenOutTitleIds);
        if (currentRequestId !== titleRequestIdRef.current) return;
        setTitleData(result);
      } catch (err) {
        if (currentRequestId !== titleRequestIdRef.current) return;
        console.error('Failed to fetch title data:', err);
        // On a soft refetch keep the current chart rather than blanking it;
        // only a hard load surfaces the empty/error state.
        if (!isSoftRefetch) setTitleData(null);
      } finally {
        if (currentRequestId === titleRequestIdRef.current) {
          setIsLoadingTitleData(false);
        }
      }
    };

    fetchTitleData();
    // brokenOutTitleIds is reducer-derived (referentially stable until changed),
    // so toggling a break-out refetches with the new set. Keyed by its contents
    // to be robust against any incidental new-array identity.
  }, [apiClient, filters, drillDownType, drillDownSubType, titleId, brokenOutTitleIds.join(',')]);

  // Only expose subData when it matches the current drillDownType to prevent
  // stale data from a previous category triggering auto-skip on re-render
  const effectiveSubData = (drillDownType && subDataForType === drillDownType) ? subData : null;

  // Compute stable color map for sub-types (based on total value across ALL years)
  const subColorMap = useMemo(() => {
    if (!effectiveSubData || effectiveSubData.length === 0) return null;

    // Aggregate total value per sub-type across all years
    const totals = new Map<string, number>();
    effectiveSubData.forEach(d => {
      const value = d.total_ist ?? d.total_soll;
      totals.set(d.expenditure_type, (totals.get(d.expenditure_type) || 0) + value);
    });

    // Sort by total descending
    const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1]);

    // Assign colors by sorted position
    const colorMap = new Map<string, string>();
    sorted.forEach(([type], index) => {
      colorMap.set(type, getSubTypeColor(index));
    });

    return colorMap;
  }, [effectiveSubData]);

  // Compute stable color map for titles (based on total value across ALL years)
  const titleColorMap = useMemo(() => {
    if (!titleData || titleData.length === 0) return null;

    // Aggregate total value per title ID across all years
    const totals = new Map<string, number>();
    titleData.forEach(d => {
      const value = d.total_ist ?? d.total_soll;
      totals.set(d.id, (totals.get(d.id) || 0) + value);
    });

    // Sort by total descending
    const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1]);

    // Assign colors (SONSTIGES gets special color)
    const colorMap = new Map<string, string>();
    let colorIndex = 0;
    sorted.forEach(([id]) => {
      if (id === SONSTIGES_ID) {
        colorMap.set(id, SONSTIGES_COLOR);
      } else {
        colorMap.set(id, getSubTypeColor(colorIndex++));
      }
    });

    return colorMap;
  }, [titleData]);

  return {
    data,
    subData: effectiveSubData,
    titleData,
    subColorMap,
    titleColorMap,
    isLoading,
    isLoadingSubData,
    isLoadingTitleData,
    error,
    refetch: fetchData,
  };
}
